import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';
import { MedicalReportProvider } from '../../../types';

const COLLECTION_NAME = 'medicalProviders';

// Convert Firestore document to MedicalReportProvider
const toProvider = (doc: any): MedicalReportProvider => {
  const data = doc.data();
  return {
    id: doc.id,
    name: data.name || '',
    nameAr: data.nameAr || '',
    licenseNumber: data.licenseNumber || '',
    vatNumber: data.vatNumber || '',
    iban: data.iban || '',
    cashbackPercentage: data.cashbackPercentage || undefined,
    departments: data.departments || [],
    isActive: data.isActive ?? true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    createdBy: data.createdBy,
  };
};

// Get all providers
export const getMedicalProviders = async (): Promise<MedicalReportProvider[]> => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('name'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toProvider);
};

// Subscribe to providers (real-time updates)
export const subscribeMedicalProviders = (
  callback: (providers: MedicalReportProvider[]) => void
) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('name'));
  return onSnapshot(q, (snapshot) => {
    const providers = snapshot.docs.map(toProvider);
    callback(providers);
  });
};

// Add a new provider
export const addMedicalProvider = async (
  provider: Omit<MedicalReportProvider, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...provider,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

// Update a provider
export const updateMedicalProvider = async (
  id: string,
  updates: Partial<Omit<MedicalReportProvider, 'id' | 'createdAt'>>
): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

// Delete a provider
export const deleteMedicalProvider = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
};

// Hook for using providers
import { useState, useEffect } from 'react';

export const useMedicalProviders = () => {
  const [providers, setProviders] = useState<MedicalReportProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeMedicalProviders((data) => {
      setProviders(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { providers, loading, error };
};
